import { useMemo, useRef } from 'react';
import { CapTableVersionWithCompanyId, ChildCompany } from 'common/types/company';
import { UnitAwareInputPolicy } from 'components/Spreadsheet/utilities';
import { EvaluatedCell, EvaluatedCellSheetMap } from 'models/interfaces/spreadsheet-types/core-types';
import {
  EXIT_EQUITY_ALIAS,
  LESS_DEBT_ALIAS,
  PLUS_CASH_ALIAS,
} from 'pages/Waterfall/SpreadsheetConfig/constants';
import {
  applyRelatedEntityEquityValidation,
  createRelatedEntityInputSheetConfig,
  getRelatedEntityIdFromInputSheetName,
} from 'pages/Waterfall/SpreadsheetConfig/RelatedEntityInputSheetConfig/relatedEntityInputSheetConfig';
import { RelatedEntityAdjustments } from 'pages/Waterfall/utilities/relatedEntityAdjustments';

interface UseRelatedEntityInputSheetsParams {
  childrenCapTables: CapTableVersionWithCompanyId[];
  childrenCompanies: ChildCompany[];
  exchangeRate: number | null;
  inputCurrencyCode?: string;
  inputPolicy: UnitAwareInputPolicy;
  isAdditionalCurrencySelected: boolean;
  outputCurrencyCode?: string;
  relatedEntitiesEnterpriseValues: Record<number, number> | null;
  relatedEntityAdjustments: RelatedEntityAdjustments;
  shouldRunRelatedEntitiesWaterfall: boolean;
}

interface SyncRelatedEntityInputAdjustmentsParams {
  onChange: (cell: EvaluatedCell, value: number) => unknown;
  relatedEntityAdjustments: RelatedEntityAdjustments;
  workbookCells?: EvaluatedCellSheetMap;
}

export const syncRelatedEntityInputAdjustments = ({
  onChange,
  relatedEntityAdjustments,
  workbookCells,
}: SyncRelatedEntityInputAdjustmentsParams) => {
  if (!workbookCells) {
    return;
  }

  Object.entries(workbookCells).forEach(([sheetName, cells]) => {
    const relatedEntityId = getRelatedEntityIdFromInputSheetName(sheetName);
    if (relatedEntityId === undefined) {
      return;
    }

    const adjustment = relatedEntityAdjustments[relatedEntityId] ?? { lessDebt: 0, plusCash: 0 };
    const expectedValues = {
      [PLUS_CASH_ALIAS]: adjustment.plusCash,
      [LESS_DEBT_ALIAS]: adjustment.lessDebt,
    };

    Object.entries(expectedValues).forEach(([alias, value]) => {
      const cell = Object.values(cells).find(candidate => candidate.alias === alias);
      if (cell && Number(cell.value) !== value) {
        onChange(cell, value);
      }
    });
  });
};

interface SyncRelatedEntityInputValidationParams {
  relatedEntitiesEquityValues: Record<number, number> | null;
  workbookCells?: EvaluatedCellSheetMap;
}

export const syncRelatedEntityInputValidation = ({
  relatedEntitiesEquityValues,
  workbookCells,
}: SyncRelatedEntityInputValidationParams) => {
  if (!workbookCells || !relatedEntitiesEquityValues) {
    return;
  }

  Object.entries(workbookCells).forEach(([sheetName, cells]) => {
    const relatedEntityId = getRelatedEntityIdFromInputSheetName(sheetName);
    if (relatedEntityId === undefined) {
      return;
    }

    const equityValue = relatedEntitiesEquityValues[relatedEntityId];
    const equityValueCell = Object.values(cells).find(candidate => candidate.alias === EXIT_EQUITY_ALIAS);
    if (equityValueCell && equityValue !== undefined) {
      applyRelatedEntityEquityValidation(equityValueCell, equityValue);
    }
  });
};

export const useRelatedEntityInputSheets = ({
  childrenCapTables,
  childrenCompanies,
  exchangeRate,
  inputCurrencyCode,
  inputPolicy,
  isAdditionalCurrencySelected,
  outputCurrencyCode,
  relatedEntitiesEnterpriseValues,
  relatedEntityAdjustments,
  shouldRunRelatedEntitiesWaterfall,
}: UseRelatedEntityInputSheetsParams) => {
  // Adjustment-only URL updates should not rebuild the full Waterfall workbook.
  // Keep the latest values available whenever a structural dependency legitimately creates new sheets.
  const relatedEntityAdjustmentsRef = useRef(relatedEntityAdjustments);
  relatedEntityAdjustmentsRef.current = relatedEntityAdjustments;

  return useMemo(
    () =>
      shouldRunRelatedEntitiesWaterfall && relatedEntitiesEnterpriseValues
        ? Object.entries(relatedEntitiesEnterpriseValues)
          .map(([relatedEntityIdValue, relatedEntityEnterpriseValue]) => {
            const relatedEntityId = Number(relatedEntityIdValue);
            const relatedEntity = childrenCompanies.find(
              child => Number(child.company_id) === relatedEntityId
            );
            const capTable = childrenCapTables.find(child => Number(child.company_id) === relatedEntityId);
            if (!relatedEntity || !capTable || capTable.is_empty) {
              return null;
            }
            const adjustment = relatedEntityAdjustmentsRef.current[relatedEntityId] ?? {
              lessDebt: 0,
              plusCash: 0,
            };

            return {
              companyId: relatedEntityId,
              inputSheet: createRelatedEntityInputSheetConfig({
                companyId: relatedEntityId,
                companyName: relatedEntity.company_name,
                enterpriseValue: relatedEntityEnterpriseValue,
                exchangeRate,
                inputCurrencyCode,
                inputPolicy,
                isAdditionalCurrencySelected,
                lessDebt: adjustment.lessDebt,
                outputCurrencyCode,
                plusCash: adjustment.plusCash,
              }),
              relatedEntity,
            };
          })
          .filter(entry => entry !== null)
        : [],
    [
      childrenCapTables,
      childrenCompanies,
      exchangeRate,
      inputCurrencyCode,
      inputPolicy,
      isAdditionalCurrencySelected,
      outputCurrencyCode,
      relatedEntitiesEnterpriseValues,
      shouldRunRelatedEntitiesWaterfall,
    ]
  );
};
